import React from "react";

import { Delta } from "./Delta";
import { Icon, type IconName } from "./icons";
import { InfoDot, InsideControl } from "./Tooltip";

export type IconTone = "blue" | "green" | "violet" | "amber" | "cyan" | "rose";

/** Tinted square that carries a card's icon. */
export function IconTile({ icon, tone }: { icon: IconName; tone: IconTone }) {
  return (
    <span className={`ds-icotile ${tone}`} aria-hidden>
      <Icon name={icon} size={14} />
    </span>
  );
}

export function Card({
  title,
  icon,
  info,
  actions,
  flush = false,
  className,
  children,
  as: Tag = "section",
}: {
  title?: React.ReactNode;
  icon?: React.ReactNode;
  info?: string;
  actions?: React.ReactNode;
  /** No body padding, for a table that runs edge to edge. */
  flush?: boolean;
  className?: string;
  children?: React.ReactNode;
  as?: "section" | "div";
}) {
  return (
    <Tag
      className={`ds-card${flush ? " flush" : ""}${className ? ` ${className}` : ""}`}
    >
      {(title || actions) && (
        <header className="ds-card-head">
          <h2 className="ds-card-title">
            {icon}
            {title}
            {info && <InfoDot text={info} />}
          </h2>
          {actions && <div className="ds-card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </Tag>
  );
}

/** One KPI: label, big number, change, and an optional trend underneath. */
export function StatTile({
  label,
  value,
  suffix,
  delta,
  note,
  spark,
  onClick,
  info,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  suffix?: string;
  delta?: React.ReactNode;
  note?: React.ReactNode;
  spark?: React.ReactNode;
  /** Opens another view; a chevron at the end of the label row says so. */
  onClick?: () => void;
  info?: string;
  /** Colours the value when the figure itself is the alarm (over a cap). */
  tone?: "warn" | "bad";
}) {
  // A change figure sits on the value's baseline, as in ``Figure``; a status
  // pill keeps the top-right corner.
  const change = React.isValidElement(delta) && delta.type === Delta;
  const body = (
    <>
      <span className="ds-stat-top">
        <span className="ds-stat-label">
          {label}
          {info && <InfoDot text={info} />}
        </span>
        {!change && delta}
        {onClick && (
          <Icon name="chevron-right" size={14} className="ds-stat-go" />
        )}
      </span>
      <span className="ds-stat-value">
        {value}
        {suffix && " "}
        {suffix && <span className="ds-stat-suffix">{suffix}</span>}
        {change && delta}
      </span>
      {note && <span className="ds-stat-note">{note}</span>}
      {spark && <span className="ds-stat-spark">{spark}</span>}
    </>
  );
  const t = tone ? ` ${tone}` : "";
  if (!onClick) return <div className={`ds-stat${t}`}>{body}</div>;
  return (
    <button type="button" className={`ds-stat click${t}`} onClick={onClick}>
      <InsideControl.Provider value>{body}</InsideControl.Provider>
    </button>
  );
}

/** A row of display-only figures in the same boxes as StatTile; one row when
 * it fits, three columns when narrower, two on phones. */
export function StatStrip({ children }: { children: React.ReactNode }) {
  return (
    <div className="ds-statstrip-box">
      <div className="ds-statstrip">{children}</div>
    </div>
  );
}

/** A label above a big number, for the inside of a card. */
export function Figure({
  label,
  value,
  suffix,
  delta,
  note,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  suffix?: string;
  delta?: React.ReactNode;
  note?: React.ReactNode;
  /** Colours the value when it is past a stated limit. */
  tone?: "warn" | "bad";
}) {
  return (
    <div className={`ds-figure${tone ? ` ${tone}` : ""}`}>
      <span className="ds-figure-label">{label}</span>
      <span className="ds-figure-value">
        {value}
        {suffix && " "}
        {suffix && <span className="ds-stat-suffix">{suffix}</span>}
        {delta}
      </span>
      {note && <span className="ds-figure-note">{note}</span>}
    </div>
  );
}
